// Component-level CSS rules, layered on top of tokens.ts inside the embed's Shadow DOM stylesheet.
// Every value here resolves from a var(--bfc-*) token declared in tokens.ts — no hard-coded brand
// value, per the design-token contract's own rule
// (docs/design/2026-08-26-design-token-contract.md).

export const componentStylesheet = `
/* Every primary-action button carries .bfc-btn, which owns the gradient, type, radius, hover and
   focus treatment in one place. The classes below it (.bfc-cta, .bfc-start, .bfc-continue,
   .bfc-book) only describe how one button differs: size, width, placement. */
.bfc-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--bfc-space-sm);
  background: var(--bfc-gradient-cta);
  color: #fff;
  font-family: var(--bfc-font-family);
  font-weight: var(--bfc-font-weight-black);
  font-size: var(--bfc-font-size-label);
  letter-spacing: var(--bfc-letter-spacing-label);
  text-transform: uppercase;
  text-decoration: none;
  border: none;
  border-radius: var(--bfc-radius-button);
  padding: var(--bfc-space-md) var(--bfc-space-lg);
  cursor: pointer;
  transition: transform var(--bfc-motion-duration-fast) var(--bfc-motion-ease),
    box-shadow var(--bfc-motion-duration-fast) var(--bfc-motion-ease),
    opacity var(--bfc-motion-duration-fast) var(--bfc-motion-ease);
}
.bfc-btn:hover:not(:disabled) {
  background: var(--bfc-gradient-cta-hover);
  transform: translateY(-1px);
}
.bfc-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.bfc-btn:focus-visible {
  outline: 2px solid var(--bfc-color-focus-ring);
  outline-offset: 2px;
}

/* The page-level CTA lifts a little more than the buttons inside the overlay. */
.bfc-cta:hover:not(:disabled) {
  transform: translateY(-2px) scale(1.02);
  box-shadow: var(--bfc-shadow-card);
}
.bfc-cta:active {
  transform: translateY(0) scale(0.99);
}

.bfc-alert {
  padding: var(--bfc-space-md);
  border-radius: var(--bfc-radius-md);
  border: 1px solid var(--bfc-color-warning);
  font-size: var(--bfc-font-size-label);
}
.bfc-alert--error {
  background: var(--bfc-color-error-50);
  color: var(--bfc-color-text);
}
.bfc-alert--success {
  background: var(--bfc-color-success-50);
  color: var(--bfc-color-text);
}
.bfc-alert--warning {
  background: var(--bfc-color-warning-50);
  color: var(--bfc-color-text);
}

.bfc-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--bfc-z-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bfc-color-scrim);
  padding: var(--bfc-space-lg);
}

.bfc-panel {
  width: 100%;
  max-width: var(--bfc-overlay-max-width);
  max-height: 88vh;
  overflow-y: auto;
  background: var(--bfc-color-bg);
  border-radius: var(--bfc-radius-lg);
  box-shadow: var(--bfc-shadow-panel);
  padding: var(--bfc-space-xl) var(--bfc-space-lg) var(--bfc-space-lg);
  position: relative;
  /* Inherited properties cross the shadow boundary, so without these the panel's text picks up
     whatever the host page styles <bfc-discovery> with (e.g. a theme's .btn: white text). */
  color: var(--bfc-color-text);
  font-family: var(--bfc-font-family);
  font-size: var(--bfc-font-size-body);
  font-weight: var(--bfc-font-weight-regular);
  line-height: 1.5;
  letter-spacing: normal;
  text-transform: none;
  text-align: left;
}
/* The intro's photo runs to the panel's edges, so the panel drops its own padding while it shows
   and the intro supplies its own. */
.bfc-panel--intro {
  padding: 0;
}
/* Media queries can't read custom properties, so this one breakpoint is a literal. */
@media (max-width: 480px) {
  .bfc-overlay {
    padding: var(--bfc-space-md);
  }
  .bfc-panel {
    padding: var(--bfc-space-xl) var(--bfc-space-md) var(--bfc-space-md);
  }
  .bfc-panel--intro {
    padding: 0;
  }
}

.bfc-close {
  position: absolute;
  top: var(--bfc-space-md);
  right: var(--bfc-space-md);
  z-index: 2;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(31, 43, 68, 0.08);
  border: none;
  color: var(--bfc-color-text-muted);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  border-radius: 50%;
  transition: color var(--bfc-motion-duration-fast) var(--bfc-motion-ease),
    background var(--bfc-motion-duration-fast) var(--bfc-motion-ease);
}
.bfc-close:hover {
  color: var(--bfc-color-text);
  background: rgba(31, 43, 68, 0.16);
}
.bfc-close:focus-visible {
  outline: 2px solid var(--bfc-color-focus-ring);
  outline-offset: 2px;
}

.bfc-progress {
  height: 4px;
  border-radius: 2px;
  background: var(--bfc-color-border);
  overflow: hidden;
  margin-bottom: var(--bfc-space-lg);
}
.bfc-progress-fill {
  height: 100%;
  background: var(--bfc-gradient-cta);
  border-radius: 2px;
  transition: width var(--bfc-motion-duration-base) var(--bfc-motion-ease);
}

.bfc-back {
  background: none;
  border: none;
  color: var(--bfc-color-text-muted);
  font-size: var(--bfc-font-size-label);
  cursor: pointer;
  padding: 0;
  margin-bottom: var(--bfc-space-md);
  display: inline-flex;
  align-items: center;
  gap: var(--bfc-space-xs);
  transition: color var(--bfc-motion-duration-fast) var(--bfc-motion-ease);
}
.bfc-back:hover {
  color: var(--bfc-color-text);
}
.bfc-back:disabled {
  visibility: hidden;
}

.bfc-flow-body {
  position: relative;
}

.bfc-flow-body-busy {
  opacity: 0.5;
  pointer-events: none;
  transition: opacity var(--bfc-motion-duration-fast) var(--bfc-motion-ease);
}

.bfc-thinking-overlay {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--bfc-space-md);
  padding: var(--bfc-space-xl) var(--bfc-space-md);
  text-align: center;
  background: var(--bfc-color-bg);
  border-radius: var(--bfc-radius-md);
}

.bfc-replan-note {
  margin: 0 0 var(--bfc-space-md);
}

/* Box styling (padding/border/background/color) now comes from .bfc-alert/.bfc-alert--error,
 * applied alongside this class in question-flow.ts — this rule only adds the spacing this
 * particular alert needs beyond the shared base. */
.bfc-persist-error {
  margin-top: var(--bfc-space-md);
}
.bfc-persist-error p {
  margin: 0 0 var(--bfc-space-sm);
}

.bfc-question {
  font-size: var(--bfc-font-size-question);
  font-weight: var(--bfc-font-weight-bold);
  line-height: 1.3;
  margin: 0 0 var(--bfc-space-lg);
}

.bfc-options {
  display: flex;
  flex-direction: column;
  gap: var(--bfc-space-sm);
}

.bfc-option {
  display: flex;
  align-items: center;
  width: 100%;
  text-align: left;
  background: var(--bfc-color-surface);
  border: 1px solid var(--bfc-color-border);
  border-radius: var(--bfc-radius-md);
  padding: var(--bfc-space-md);
  font-family: var(--bfc-font-family);
  font-size: var(--bfc-font-size-body);
  color: var(--bfc-color-text);
  cursor: pointer;
  transition: border-color var(--bfc-motion-duration-fast) var(--bfc-motion-ease),
    background var(--bfc-motion-duration-fast) var(--bfc-motion-ease),
    transform var(--bfc-motion-duration-fast) var(--bfc-motion-ease);
}
.bfc-option:hover {
  border-color: var(--bfc-color-accent);
  transform: translateX(2px);
}
.bfc-option:focus-visible {
  outline: 2px solid var(--bfc-color-focus-ring);
  outline-offset: 2px;
}
.bfc-option[aria-pressed="true"] {
  border-color: var(--bfc-color-accent);
  background: color-mix(in srgb, var(--bfc-color-accent) 8%, var(--bfc-color-surface));
}

.bfc-option-none {
  background: none;
  border: 1px dashed var(--bfc-color-border);
  color: var(--bfc-color-text-muted);
}

.bfc-pii-input {
  width: 100%;
  font-family: var(--bfc-font-family);
  font-size: var(--bfc-font-size-body);
  color: var(--bfc-color-text);
  background: var(--bfc-color-surface);
  border: 1px solid var(--bfc-color-border);
  border-radius: var(--bfc-radius-md);
  padding: var(--bfc-space-md);
  margin-bottom: var(--bfc-space-md);
}
.bfc-pii-input:focus-visible {
  outline: 2px solid var(--bfc-color-focus-ring);
  outline-offset: 2px;
  border-color: var(--bfc-color-accent);
}

/* .bfc-continue needs no rules of its own; it is a plain .bfc-btn, and the class stays as a hook. */

.bfc-pii-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--bfc-space-md);
}

.bfc-optout {
  background: none;
  border: none;
  color: var(--bfc-color-text-muted);
  font-size: var(--bfc-font-size-label);
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
}
.bfc-optout:hover {
  color: var(--bfc-color-text);
}

.bfc-privacy-note {
  color: var(--bfc-color-text-muted);
  font-size: var(--bfc-font-size-label);
  line-height: 1.5;
  margin: var(--bfc-space-md) 0 0;
}
.bfc-privacy-note a {
  color: inherit;
  text-decoration: underline;
}
.bfc-privacy-note a:hover {
  color: var(--bfc-color-text);
}

.bfc-waiting {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--bfc-space-md);
  padding: var(--bfc-space-xl) 0;
  text-align: center;
  color: var(--bfc-color-text-muted);
}

/* Intro screen: photo bleeds to the panel edges and fades into the panel's background. */
.bfc-intro-hero {
  height: 300px;
  padding-top: var(--bfc-space-md);
  overflow: hidden;
  background: linear-gradient(180deg, var(--bfc-color-surface), var(--bfc-color-bg));
}
.bfc-intro-photo {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 0;
  -webkit-mask-image: linear-gradient(to bottom, #000 55%, transparent 100%);
  mask-image: linear-gradient(to bottom, #000 55%, transparent 100%);
}
.bfc-intro-body {
  padding: 0 var(--bfc-space-lg) var(--bfc-space-lg);
  text-align: center;
}
.bfc-intro-title {
  font-size: 26px;
  font-weight: var(--bfc-font-weight-bold);
  line-height: 1.2;
  margin: 0 0 var(--bfc-space-md);
}
.bfc-intro-copy {
  font-size: 15px;
  line-height: 1.55;
  margin: 0 0 var(--bfc-space-lg);
}
.bfc-start {
  display: flex;
  width: 100%;
}

/* The shared loading treatment (loading.ts): the host's photo inside a gold ring that spins. */
.bfc-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--bfc-space-md);
  padding: var(--bfc-space-lg) 0;
  text-align: center;
}
.bfc-avatar {
  position: relative;
  flex: none;
  width: var(--bfc-avatar-size);
  height: var(--bfc-avatar-size);
}
/* The track the ring travels around. */
.bfc-avatar::before {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 3px solid var(--bfc-color-border);
}
.bfc-avatar-ring {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: conic-gradient(from 0deg, transparent 0deg, var(--bfc-color-cta) 110deg, var(--bfc-color-cta-end) 300deg, transparent 360deg);
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2.5px));
  mask: radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 2.5px));
  animation: bfc-spin 1300ms linear infinite;
}
@media (prefers-reduced-motion: reduce) {
  .bfc-avatar-ring {
    animation-duration: 2600ms;
  }
}
@keyframes bfc-spin {
  to { transform: rotate(360deg); }
}
.bfc-avatar-disc {
  position: absolute;
  inset: 8px;
  border-radius: 50%;
  overflow: hidden;
  background: var(--bfc-color-avatar-bg);
}
.bfc-avatar-photo {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 0;
}
.bfc-loading-heading {
  font-size: var(--bfc-font-size-question);
  font-weight: var(--bfc-font-weight-bold);
  line-height: 1.3;
  margin: 0;
}
.bfc-loading-note {
  color: var(--bfc-color-text-muted);
  font-size: var(--bfc-font-size-label);
  margin: 0;
}

.bfc-steps {
  list-style: none;
  margin: var(--bfc-space-sm) 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: left;
}
.bfc-step {
  position: relative;
  padding-left: 24px;
  font-size: var(--bfc-font-size-label);
  color: var(--bfc-color-text-muted);
  transition: color var(--bfc-motion-duration-base) var(--bfc-motion-ease);
}
.bfc-step::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0.35em;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--bfc-color-border);
  transition: background var(--bfc-motion-duration-base) var(--bfc-motion-ease);
}
.bfc-step--done::before {
  background: var(--bfc-color-accent);
}
.bfc-step--active {
  color: var(--bfc-color-text);
  font-weight: var(--bfc-font-weight-bold);
}
.bfc-step--active::before {
  background: var(--bfc-color-cta);
}

.bfc-result-heading {
  font-size: var(--bfc-font-size-heading);
  font-weight: var(--bfc-font-weight-bold);
  line-height: 1.3;
  margin: 0 0 var(--bfc-space-md);
}

.bfc-reasoning {
  color: var(--bfc-color-text-muted);
  line-height: 1.5;
  margin: 0 0 var(--bfc-space-lg);
}

/* Result screen: small caps eyebrow labels over a two-step numbered timeline, then the match card. */
.bfc-eyebrow {
  font-size: var(--bfc-font-size-eyebrow);
  font-weight: var(--bfc-font-weight-black);
  letter-spacing: var(--bfc-letter-spacing-eyebrow);
  text-transform: uppercase;
  color: var(--bfc-color-eyebrow);
  margin: 0 0 var(--bfc-space-md);
}
.bfc-eyebrow--small {
  font-size: 11px;
  margin: 0 0 6px;
}

.bfc-timeline {
  list-style: none;
  margin: 0 0 var(--bfc-space-lg);
  padding: 0;
}
.bfc-timeline-item {
  position: relative;
  padding-left: 44px;
  padding-bottom: var(--bfc-space-lg);
}
.bfc-timeline-item:last-child {
  padding-bottom: 0;
}
/* The rule connecting one badge to the next. */
.bfc-timeline-item:not(:last-child)::before {
  content: "";
  position: absolute;
  left: 13px;
  top: 34px;
  bottom: 6px;
  width: 2px;
  background: var(--bfc-color-border);
}
.bfc-timeline-badge {
  position: absolute;
  left: 0;
  top: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--bfc-gradient-cta);
  color: #fff;
  font-size: var(--bfc-font-size-label);
  font-weight: var(--bfc-font-weight-black);
}
.bfc-timeline-item .bfc-eyebrow--small {
  padding-top: 8px;
}
.bfc-timeline-text {
  font-size: 18px;
  font-weight: var(--bfc-font-weight-bold);
  line-height: 1.4;
  margin: 0;
}
.bfc-timeline-text--soft {
  font-size: var(--bfc-font-size-body);
  font-weight: var(--bfc-font-weight-regular);
  color: var(--bfc-color-text-muted);
  line-height: 1.55;
}

.bfc-result-card {
  background: var(--bfc-color-surface);
  border: 1px solid var(--bfc-color-border);
  border-radius: 16px;
  padding: var(--bfc-space-lg) 28px;
}

.bfc-result-name {
  font-size: var(--bfc-font-size-question);
  font-weight: var(--bfc-font-weight-bold);
  margin: 0 0 var(--bfc-space-sm);
}

.bfc-result-oneliner {
  color: var(--bfc-color-text-muted);
  line-height: 1.5;
  margin: 0 0 var(--bfc-space-lg);
}

.bfc-result-attrs {
  font-size: var(--bfc-font-size-label);
  color: var(--bfc-color-text-muted);
  margin: 0 0 var(--bfc-space-lg);
}

.bfc-result-attr {
  display: block;
  margin: 0 0 var(--bfc-space-xs);
}

/* Campaign offer (docs/plans/2026-10-13-campaigns.md). Sits right above the booking button it
   applies to; mirrors .bfc-report-campaign in the downloadable report. */
.bfc-result-campaign {
  border: 1px dashed var(--bfc-color-eyebrow);
  border-radius: var(--bfc-radius-md);
  padding: 10px var(--bfc-space-md) 12px;
  margin: 0 0 var(--bfc-space-md);
  text-align: left;
}
.bfc-result-campaign-text {
  font-size: var(--bfc-font-size-label);
  font-weight: var(--bfc-font-weight-bold);
  line-height: 1.45;
  margin: 0;
}

/* Other ranked Board Members, shown below the top match. Deliberately quieter than the primary
   card (no shadow, lighter border, outline CTA) so they read as "worth a look" rather than
   competing with the top match — same secondary treatment as the downloadable report's
   .bfc-report-card-secondary (src/session/report-template.ts). */
.bfc-result-others {
  margin-top: var(--bfc-space-lg);
  padding-top: var(--bfc-space-md);
  border-top: 1px solid var(--bfc-color-border);
}

.bfc-result-others-toggle {
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: var(--bfc-space-sm);
  width: 100%;
  background: none;
  border: none;
  padding: 0 0 var(--bfc-space-md);
  margin: 0;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.bfc-result-others-label {
  font-size: var(--bfc-font-size-label);
  font-weight: var(--bfc-font-weight-black);
  letter-spacing: var(--bfc-letter-spacing-label);
  text-transform: uppercase;
  color: var(--bfc-color-text-muted);
}

.bfc-result-others-cta {
  display: block;
  text-transform: none;
  animation: bfc-fade-in var(--bfc-motion-duration-base) var(--bfc-motion-ease) both;
}
.bfc-result-others-toggle-open .bfc-result-others-cta {
  animation: bfc-fade-out var(--bfc-motion-duration-base) var(--bfc-motion-ease) both;
}

/* Full-strength text color against the muted label around it, so the count reads as "there is
   something here" at a glance rather than blending into the section heading (Thomas 2026-09-10:
   the collapsed toggle was too easy to miss and read as "no other options" rather than
   "collapsed"). Not uppercase/letter-spaced like the label — it's a number, not a heading word. */
.bfc-result-others-count {
  color: var(--bfc-color-text);
  letter-spacing: normal;
}

.bfc-result-others-chevron {
  flex: none;
  width: 10px;
  height: 10px;
  border-right: 2.5px solid var(--bfc-color-text);
  border-bottom: 2.5px solid var(--bfc-color-text);
  transform: rotate(45deg);
  transition: transform var(--bfc-motion-duration-base) var(--bfc-motion-ease);
}

.bfc-result-others-toggle-open .bfc-result-others-chevron {
  transform: rotate(-135deg);
}

.bfc-result-others-body {
  overflow: hidden;
  max-height: 0;
  transition: max-height var(--bfc-motion-duration-base) var(--bfc-motion-ease);
}

.bfc-result-others-body-inner {
  padding-top: var(--bfc-space-xs);
}

.bfc-result-others-subhead {
  color: var(--bfc-color-text-muted);
  font-size: var(--bfc-font-size-label);
  line-height: 1.5;
  margin: 0 0 var(--bfc-space-sm);
}

.bfc-result-card-secondary {
  background: transparent;
  box-shadow: none;
  padding: var(--bfc-space-md) var(--bfc-space-lg);
  margin-bottom: var(--bfc-space-md);
}

.bfc-result-card-secondary .bfc-result-name {
  font-size: var(--bfc-font-size-body);
  margin-bottom: var(--bfc-space-xs);
}

.bfc-result-card-secondary .bfc-result-oneliner,
.bfc-result-card-secondary .bfc-result-attrs {
  margin-bottom: var(--bfc-space-md);
}

/* The matcher's "why this one, and why not first" line. Full text color rather than muted, so it
   reads as the answer to the visitor's question rather than more card metadata. */
.bfc-result-alt-reason {
  font-size: var(--bfc-font-size-label);
  line-height: 1.5;
  margin: 0 0 var(--bfc-space-md);
}

/* Placeholder while the alternates call is in flight: two lines' worth of quiet shimmer, replaced
   by the reason text in place, or removed if no reason arrives. */
.bfc-result-alt-reason-pending {
  min-height: 3em;
  border-radius: 4px;
  background: linear-gradient(90deg, var(--bfc-color-border) 25%, var(--bfc-color-bg) 50%, var(--bfc-color-border) 75%);
  background-size: 200% 100%;
  animation: bfc-shimmer 1400ms ease-in-out infinite;
}
@media (prefers-reduced-motion: reduce) {
  .bfc-result-alt-reason-pending {
    animation: none;
  }
}
@keyframes bfc-shimmer {
  from { background-position: 100% 0; }
  to { background-position: -100% 0; }
}

.bfc-result-card-secondary .bfc-book {
  background: none;
  color: var(--bfc-color-accent);
  border: 1px solid var(--bfc-color-accent);
  padding: var(--bfc-space-sm) var(--bfc-space-md);
}

.bfc-result-card-secondary .bfc-book:hover:not(:disabled) {
  background: color-mix(in srgb, var(--bfc-color-accent) 10%, transparent);
}
.bfc-report:focus-visible {
  outline: 2px solid var(--bfc-color-focus-ring);
  outline-offset: 2px;
}

/* Inside a match card the booking button is the full-width primary action, in sentence-style
   caps rather than the shouted uppercase the standalone buttons use. */
.bfc-result-card .bfc-book {
  width: 100%;
  text-transform: none;
  letter-spacing: normal;
  font-weight: var(--bfc-font-weight-bold);
  text-align: center;
}

/* The report is a quiet text link under the booking button, not a second button competing with it. */
.bfc-report {
  display: block;
  margin-top: var(--bfc-space-md);
  text-align: center;
  color: var(--bfc-color-accent);
  font-family: var(--bfc-font-family);
  font-weight: var(--bfc-font-weight-bold);
  font-size: var(--bfc-font-size-label);
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}
.bfc-report:hover {
  color: var(--bfc-color-text);
}

/* "Got another problem?" sits below the match as a quiet text button, the same weight as the
   report link, so it never competes with booking. */
.bfc-restart {
  display: block;
  margin: var(--bfc-space-lg) auto 0;
  background: none;
  border: none;
  padding: var(--bfc-space-sm);
  color: var(--bfc-color-text-muted);
  font-family: var(--bfc-font-family);
  font-size: var(--bfc-font-size-label);
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;
}
.bfc-restart:hover {
  color: var(--bfc-color-text);
}
.bfc-restart:focus-visible {
  outline: 2px solid var(--bfc-color-focus-ring);
  outline-offset: 2px;
}

/* Replaces the result screen's content (embed.ts's showCloseConfirm) rather than sitting below
   it, so no top margin here — it's the panel's only content while showing, not a second block
   stacked under the match card. */
.bfc-close-confirm {
  padding: var(--bfc-space-lg);
  background: var(--bfc-color-surface);
  border: 1px solid var(--bfc-color-border);
  border-radius: var(--bfc-radius-md);
  box-shadow: var(--bfc-shadow-card);
}

.bfc-close-confirm-heading {
  font-size: var(--bfc-font-size-question);
  font-weight: var(--bfc-font-weight-bold);
  margin: 0 0 var(--bfc-space-sm);
}

.bfc-close-confirm-body {
  color: var(--bfc-color-text-muted);
  line-height: 1.5;
  margin: 0 0 var(--bfc-space-md);
}

.bfc-close-confirm-actions {
  display: flex;
  gap: var(--bfc-space-sm);
  flex-wrap: wrap;
}

.bfc-close-anyway {
  background: none;
  border: none;
  color: var(--bfc-color-text-muted);
  font-size: var(--bfc-font-size-label);
  text-decoration: underline;
  cursor: pointer;
  padding: var(--bfc-space-md) var(--bfc-space-sm);
}
.bfc-close-anyway:hover {
  color: var(--bfc-color-text);
}
.bfc-close-anyway:focus-visible {
  outline: 2px solid var(--bfc-color-focus-ring);
  outline-offset: 2px;
}

.bfc-fade-enter {
  animation: bfc-fade-in var(--bfc-motion-duration-base) var(--bfc-motion-ease) both;
}
@keyframes bfc-fade-in {
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes bfc-fade-out {
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(6px); }
}

.bfc-optout-screen {
  text-align: center;
  padding: var(--bfc-space-xl) 0;
}
`;
